import type { PricingContent } from '@mawkaa/types';
import { Check } from 'lucide-react';

interface PricingBlockProps {
  content: PricingContent;
  isEditing?: boolean;
}

export function PricingBlock({ content, isEditing }: PricingBlockProps) {
  const { title, subtitle, plans = [] } = content;

  if (plans.length === 0) {
    return (
      <section className="px-4 py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold">{title || 'Nos Tarifs'}</h2>
            {subtitle && <p className="mt-4 text-lg text-muted-foreground">{subtitle}</p>}
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {['Basic', 'Pro', 'Enterprise'].map((name) => (
              <div key={name} className="border rounded-2xl p-8 text-center">
                <h3 className="text-xl font-semibold mb-2">{name}</h3>
                <div className="text-3xl font-bold mb-4">-- DT</div>
                <ul className="space-y-3 mb-8 text-left">
                  {[1, 2, 3].map((i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-500" />
                      <span className="text-gray-400">Fonctionnalité {i}</span>
                    </li>
                  ))}
                </ul>
                <button className="w-full py-3 px-6 border rounded-lg font-medium">
                  Choisir
                </button>
              </div>
            ))}
          </div>
          {isEditing && (
            <p className="text-center text-muted-foreground mt-8">
              Configurez vos plans tarifaires
            </p>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="px-4 py-16">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold">{title}</h2>
          {subtitle && <p className="mt-4 text-lg text-muted-foreground">{subtitle}</p>}
        </div>
        <div className={`grid gap-8 ${plans.length === 1 ? 'max-w-md mx-auto' : plans.length === 2 ? 'md:grid-cols-2 max-w-4xl mx-auto' : 'md:grid-cols-3'}`}>
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`border rounded-2xl p-8 text-center relative ${plan.highlighted ? 'border-primary ring-2 ring-primary' : ''}`}
            >
              {plan.highlighted && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-sm px-4 py-1 rounded-full">
                  Populaire
                </span>
              )}
              <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
              <div className="text-4xl font-bold mb-1">{plan.price}</div>
              {plan.period && <div className="text-muted-foreground mb-6">{plan.period}</div>}
              <ul className="space-y-3 mb-8 text-left">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <a
                href={plan.buttonUrl || '#'}
                className={`block w-full py-3 px-6 rounded-lg font-medium transition-colors ${
                  plan.highlighted
                    ? 'bg-primary text-white hover:bg-primary/90'
                    : 'border hover:bg-gray-50'
                }`}
              >
                {plan.buttonText || 'Choisir'}
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
